import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { downloadProposalPdf } from "@/lib/proposalPdfExport";
import { proposalQuotationPdfApiPath } from "@shared/proposalPdfFilename";
import { PROPOSAL_PDF_DOWNLOAD_LABEL_CUSTOMER } from "@shared/proposalCustomerEngagement";
import { readDtoolsDisplaySettings } from "@shared/dtoolsDisplaySettings";
import GenerateProposalPdfDialog from "@/components/proposal/GenerateProposalPdfDialog";
import { Download, Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export const GENERATING_LABEL = "Generating PDF...";
/** @deprecated Legacy html2canvas path only; kept for printOnly sandbox. */
export const OPTIMIZING_LABEL = "Optimising large PDF...";

/** Delay before revoking blob URLs after download / successful _blank open. */
export const BLOB_URL_REVOKE_DELAY_MS = 60_000;

type Props = {
  proposalId: number;
  proposalRef?: string | null;
  customerEmail?: string | null;
  className?: string;
  variant?:
    | "default"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "destructive";
  label?: string;
  /** Staff Link A / sandbox — open print without recording customer engagement. */
  skipAnalytics?: boolean;
  /** Print only — no proposal id required (workspace sandbox); uses legacy DOM export. */
  printOnly?: boolean;
  /**
   * When true, open the server quotation PDF immediately
   * (used by Deal Workspace “View PDF” → preview `?viewPdf=view`, or auto-open in preview tab).
   */
  autoStart?: boolean;
  /** Called after an autoStart attempt finishes (success or failure). */
  onAutoStartSettled?: () => void;
  /**
   * `download` — attachment disposition (default, customer Download PDF).
   * `open` — inline disposition in the browser PDF viewer.
   */
  delivery?: "download" | "open";
  /**
   * Staff: open Generate Proposal PDF dialog to choose show/hide item pricing for this render only.
   * Defaults to true for staff (`skipAnalytics`) when not auto-starting / printOnly.
   * Customer downloads keep the saved proposal preference.
   */
  choosePricingVisibility?: boolean;
  /** Saved proposal preference used as the dialog default. */
  defaultHideItemPricing?: boolean;
  /** Optional parsedMetadataJson — used to derive defaultHideItemPricing when not provided. */
  parsedMetadataJson?: unknown;
  /** Secure Link B credential — required for public (non-staff) customer downloads. */
  linkBCredential?: { readableRef: string; sequence: number; token: string } | null;
};

function openQuotationPdfUrl(url: string, target: "_self" | "_blank"): boolean {
  const win = window.open(
    url,
    target,
    target === "_blank" ? "noopener,noreferrer" : undefined,
  );
  return !!win;
}

async function readPdfFailureMessage(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as {
    message?: string;
  } | null;
  return body?.message || `Could not generate proposal PDF (${res.status}).`;
}

function filenameFromContentDisposition(value: string | null): string | null {
  if (!value) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(value);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(value);
  if (quoted?.[1]) return quoted[1];
  const plain = /filename=([^;]+)/i.exec(value);
  return plain?.[1]?.trim() || null;
}

async function fetchQuotationPdfBlob(
  path: string,
): Promise<{ blob: Blob; filename: string | null }> {
  const res = await fetch(path, { credentials: "include" });
  if (!res.ok) {
    throw new Error(await readPdfFailureMessage(res));
  }
  return {
    blob: await res.blob(),
    filename: filenameFromContentDisposition(
      res.headers.get("content-disposition"),
    ),
  };
}

function downloadBlobUrl(objectUrl: string, filename: string | null): void {
  const a = document.createElement("a");
  a.href = objectUrl;
  if (filename) a.download = filename;
  a.rel = "noopener noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function safeRevokeObjectUrl(url: string | null | undefined): void {
  if (!url) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    /* already revoked */
  }
}

/** Opens or downloads the server-generated quotation PDF (pdfkit). */
export default function ProposalPdfDownloadButton({
  proposalId,
  proposalRef: _proposalRef,
  customerEmail,
  className,
  variant = "outline",
  label = PROPOSAL_PDF_DOWNLOAD_LABEL_CUSTOMER,
  skipAnalytics = false,
  printOnly = false,
  autoStart = false,
  onAutoStartSettled,
  delivery = "download",
  choosePricingVisibility,
  defaultHideItemPricing,
  parsedMetadataJson,
  linkBCredential,
}: Props) {
  const recordPdfDownload =
    trpc.proposals.recordCustomerProposalPdfDownload.useMutation();
  const [busy, setBusy] = useState(false);
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  const [fallbackObjectUrl, setFallbackObjectUrl] = useState<string | null>(
    null,
  );
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const autoStartedRef = useRef(false);
  const onAutoStartSettledRef = useRef(onAutoStartSettled);
  onAutoStartSettledRef.current = onAutoStartSettled;

  const ownedObjectUrlRef = useRef<string | null>(null);
  const fallbackObjectUrlRef = useRef<string | null>(null);
  const retainForNavigationRef = useRef<Set<string>>(new Set());
  const revokeTimerIdsRef = useRef<number[]>([]);

  const setFallback = useCallback((url: string | null) => {
    fallbackObjectUrlRef.current = url;
    setFallbackObjectUrl(url);
  }, []);

  const clearRevokeTimers = useCallback(() => {
    for (const id of revokeTimerIdsRef.current) {
      window.clearTimeout(id);
    }
    revokeTimerIdsRef.current = [];
  }, []);

  const tryRevoke = useCallback((url: string | null) => {
    if (!url) return;
    if (retainForNavigationRef.current.has(url)) return;
    if (fallbackObjectUrlRef.current === url) return;
    safeRevokeObjectUrl(url);
    if (ownedObjectUrlRef.current === url) {
      ownedObjectUrlRef.current = null;
    }
  }, []);

  const replaceOwnedObjectUrl = useCallback(
    (next: string | null) => {
      const previousOwned = ownedObjectUrlRef.current;
      const previousFallback = fallbackObjectUrlRef.current;

      if (previousFallback && previousFallback !== next) {
        setFallback(null);
        if (!retainForNavigationRef.current.has(previousFallback)) {
          safeRevokeObjectUrl(previousFallback);
        }
      }

      if (
        previousOwned &&
        previousOwned !== next &&
        previousOwned !== previousFallback &&
        !retainForNavigationRef.current.has(previousOwned)
      ) {
        safeRevokeObjectUrl(previousOwned);
      }

      ownedObjectUrlRef.current = next;
    },
    [setFallback],
  );

  const scheduleRevokeOwnedUrl = useCallback(
    (url: string, delayMs = BLOB_URL_REVOKE_DELAY_MS) => {
      const id = window.setTimeout(() => {
        revokeTimerIdsRef.current = revokeTimerIdsRef.current.filter((t) => t !== id);
        tryRevoke(url);
      }, delayMs);
      revokeTimerIdsRef.current.push(id);
    },
    [tryRevoke],
  );

  useEffect(() => {
    return () => {
      clearRevokeTimers();
      const owned = ownedObjectUrlRef.current;
      const fallback = fallbackObjectUrlRef.current;
      ownedObjectUrlRef.current = null;
      fallbackObjectUrlRef.current = null;
      if (fallback && !retainForNavigationRef.current.has(fallback)) {
        safeRevokeObjectUrl(fallback);
      }
      if (
        owned &&
        owned !== fallback &&
        !retainForNavigationRef.current.has(owned)
      ) {
        safeRevokeObjectUrl(owned);
      }
    };
  }, [clearRevokeTimers]);

  const usePricingDialog =
    choosePricingVisibility ?? (skipAnalytics && !autoStart && !printOnly);

  const resolvedDefaultHide =
    typeof defaultHideItemPricing === "boolean"
      ? defaultHideItemPricing
      : readDtoolsDisplaySettings(parsedMetadataJson).hideItemPricing === true;

  const handleExport = useCallback(
    async (hideItemPricing?: boolean) => {
      if (busy) return;
      setBusy(true);
      setProgressLabel(GENERATING_LABEL);
      // Clear previous fallback / owned URL before a new attempt.
      replaceOwnedObjectUrl(null);
      try {
        const shouldRecordCustomerDownload =
          !printOnly &&
          !skipAnalytics &&
          Number.isFinite(proposalId) &&
          proposalId > 0;
        const recordSuccessfulCustomerDownload = async () => {
          if (!shouldRecordCustomerDownload || !linkBCredential) return;
          const email = customerEmail?.trim();
          try {
            await recordPdfDownload.mutateAsync({
              credential: linkBCredential,
              ...(email ? { customerEmail: email } : {}),
            });
          } catch (e: unknown) {
            toast.error(
              e instanceof Error ? e.message : "Could not record PDF download.",
            );
          }
        };

        if (printOnly) {
          if (!document.querySelector(".dtools-proposal-pdf-package")) {
            toast.error(
              "Proposal PDF is not ready yet. Please try again in a moment.",
            );
            return;
          }
          await downloadProposalPdf({
            onStatus: (status) => {
              if (status === "optimizing") setProgressLabel(OPTIMIZING_LABEL);
            },
          });
          await recordSuccessfulCustomerDownload();
          toast.success("Proposal PDF downloaded.");
          return;
        }

        if (!Number.isFinite(proposalId) || proposalId <= 0) {
          toast.error("Proposal PDF is not available.");
          return;
        }

        const path = proposalQuotationPdfApiPath(proposalId, {
          download: delivery === "download",
          ...(typeof hideItemPricing === "boolean" ? { hideItemPricing } : {}),
          ...(linkBCredential ? { linkBToken: linkBCredential.token } : {}),
        });
        const { blob, filename } = await fetchQuotationPdfBlob(path);
        const objectUrl = URL.createObjectURL(blob);
        replaceOwnedObjectUrl(objectUrl);
        await recordSuccessfulCustomerDownload();

        if (delivery === "open") {
          const target = autoStart ? "_self" : "_blank";
          if (target === "_self") {
            retainForNavigationRef.current.add(objectUrl);
            window.location.assign(objectUrl);
            toast.success("Proposal PDF ready.");
            return;
          }
          if (!openQuotationPdfUrl(objectUrl, target)) {
            setFallback(objectUrl);
            toast.error("Could not open the PDF viewer. Use Open PDF below.");
            return;
          }
          toast.success("Proposal PDF ready.");
          scheduleRevokeOwnedUrl(objectUrl);
          return;
        }

        downloadBlobUrl(objectUrl, filename);
        toast.success("Proposal PDF downloaded.");
        scheduleRevokeOwnedUrl(objectUrl);
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : "Could not generate proposal PDF.";
        toast.error(message);
      } finally {
        setBusy(false);
        setProgressLabel(null);
      }
    },
    [
      autoStart,
      busy,
      customerEmail,
      delivery,
      linkBCredential,
      printOnly,
      proposalId,
      recordPdfDownload,
      replaceOwnedObjectUrl,
      scheduleRevokeOwnedUrl,
      setFallback,
      skipAnalytics,
    ],
  );

  const handleExportRef = useRef(handleExport);
  handleExportRef.current = handleExport;

  useEffect(() => {
    if (!autoStart || autoStartedRef.current) return;
    autoStartedRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        if (!cancelled) await handleExportRef.current();
      } finally {
        if (!cancelled) onAutoStartSettledRef.current?.();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [autoStart]);

  const buttonLabel = progressLabel ?? label;

  return (
    <div className="flex flex-col items-stretch gap-2">
      <Button
        type="button"
        variant={variant}
        className={className}
        disabled={busy}
        aria-busy={busy}
        onClick={() => {
          if (usePricingDialog) {
            setPdfDialogOpen(true);
            return;
          }
          void handleExport();
        }}
        data-testid="proposal-pdf-download"
      >
        {busy ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Download className="mr-2 h-4 w-4" />
        )}
        {buttonLabel}
      </Button>
      {fallbackObjectUrl ? (
        <a
          href={fallbackObjectUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-center text-sm font-medium text-primary underline"
          data-testid="proposal-pdf-open-fallback"
          onClick={(e) => {
            // Ownership cleanup after the browser initiates navigation.
            // Keep default click so the blob can open; tests stub window.open / href.
            const url = fallbackObjectUrl;
            setFallback(null);
            scheduleRevokeOwnedUrl(url);
            // Avoid double-open when tests fire click without a real browser download manager.
            if (import.meta.env.MODE === "test") {
              e.preventDefault();
            }
          }}
        >
          Open PDF
        </a>
      ) : null}
      {usePricingDialog ? (
        <GenerateProposalPdfDialog
          open={pdfDialogOpen}
          onOpenChange={setPdfDialogOpen}
          defaultHideItemPricing={resolvedDefaultHide}
          onConfirm={({ hideItemPricing }) => {
            void handleExport(hideItemPricing);
          }}
        />
      ) : null}
    </div>
  );
}
